import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import SEO from "../components/SEO";

export default function PrivacyDelete() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(null);

  useEffect(() => {
    if (token) {
      api.get("/privacy/verify", { params: { token } })
        .then(() => setVerified(true))
        .catch(() => setVerified(false));
    }
  }, [token]);

  const submit = async () => {
    try {
      await api.post("/privacy/delete-request", { email });
      setSent(true);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="px-4 max-w-md mx-auto py-16 md:py-24">
      <SEO title="Request data deletion — Tendly" description="Request deletion of your personal data from Tendly under PIPEDA." />
      <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
        <span className="w-12 h-12 rounded-xl bg-teal-50 dark:bg-teal-900/40 text-primary flex items-center justify-center"><ShieldCheck className="w-6 h-6" /></span>
        <h1 className="mt-5 font-heading font-bold text-2xl tracking-tight">Delete my data</h1>
        {token ? (
          verified === null ? <p className="mt-3 text-sm text-muted-foreground">Verifying…</p> :
          verified ? (
            <div data-testid="delete-verified" className="mt-3">
              <CheckCircle2 className="w-8 h-8 text-primary" />
              <p className="mt-2 text-sm leading-relaxed">Your request is confirmed. Our team will delete or anonymize your data within 30 days and log the completion. Questions? tendly.concierge@gmail.com</p>
            </div>
          ) : (
            <p data-testid="delete-verify-failed" className="mt-3 text-sm text-coral-600">This verification link is invalid or already used. Submit a new request below.</p>
          )
        ) : sent ? (
          <div data-testid="delete-sent" className="mt-3">
            <CheckCircle2 className="w-8 h-8 text-primary" />
            <p className="mt-2 text-sm leading-relaxed">Check your inbox — we sent a verification link to <strong>{email}</strong>. Click it to confirm your deletion request. This proves the email is yours (PIPEDA).</p>
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">Enter your email and we'll send a verification link. Once confirmed, we delete or anonymize your leads, account, and messages within 30 days.</p>
            <div className="mt-5 space-y-3">
              <input data-testid="delete-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="you@example.com" aria-label="Email address"
                className="w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm" />
              <button data-testid="delete-submit" onClick={submit}
                className="w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm font-semibold">Send verification email</button>
            </div>
          </>
        )}
        <p className="mt-5 text-xs text-muted-foreground"><Link to="/privacy" className="text-primary hover:underline">Read the Privacy Policy</Link></p>
      </div>
    </div>
  );
}
